/**
 * Email signatures and what an outgoing reply says — Aaron's Inbox notes 2.7
 * and 2.5 (Oct 24).
 *
 * **A signature is stored HTML with merge fields in it**, `{{person.name}}`
 * and the rest of `SIGNATURE_FIELDS`, written once by an admin and rendered
 * for whoever sends: the person, their branch and the company. Signatures are
 * HTML rather than a ProseMirror document (the terms' shape) because the thing
 * people paste in from CodeTwo, Outlook or a designer is a table with inline
 * styles and a logo, and an email client reads nothing else — a document tree
 * would have to be turned back into exactly this on every send.
 *
 * **Everything here is an allow-list**, for the same reason `rich-text.ts` and
 * `terms-doc.ts` are: one person writes it and every customer's mail client
 * renders it. `sanitizeSignatureHtml` runs in the editor, on the server on
 * every save, and again over every rendered result — tags not on the list lose
 * their markup and keep their text (a `<script>`, `<style>` or `<iframe>` loses
 * its contents too), attributes not on the list are dropped, every `on…`
 * handler with them, a style keeps only the properties an email can carry and
 * never a `url(…)`, and a link must be `http`, `https`, `mailto` or `tel`.
 *
 * **Images are ours.** A signature may show the company's logo
 * (`{{company.logo}}`) or an image uploaded for signatures, both served from
 * `/api/v1/email-images/<file id>` — a public path, because a mail client
 * opening a message next year has no session. An image from anywhere else is
 * dropped: a signature is not a place for somebody else's tracking pixel, and
 * a link to a picture we do not hold is a broken box the day its host moves it.
 *
 * **An empty field takes its line with it**, the way CodeTwo's do: a line
 * (a paragraph, a row, a run between two `<br>`s) whose fields are all empty
 * for this person is left out, so somebody with no title does not get a blank
 * line where it would be — and a separator beside an empty field goes too
 * (`Tom Reyes · · Susquehanna` would be two dots for one missing word).
 *
 * The second half is the reply (2.5): Rowted stores only what a person wrote,
 * so the email that leaves is built at send time — the new words, the
 * signature, then *On …, Renee Castillo wrote:* and the earlier messages of
 * the same thread quoted, each inside the one before, the way a mail client
 * writes a reply.
 */
/**
 * Every field a signature may name — what the data actually holds: the
 * person's own record (name, title, email, phone — there is no separate
 * mobile), their home branch's details (walked up the tree for anything the
 * branch leaves blank), and the company profile.
 */
export const SIGNATURE_FIELDS = [
    { key: 'person.name', label: 'Full name', group: 'Person', kind: 'text', sample: 'Tom Reyes' },
    { key: 'person.first_name', label: 'First name', group: 'Person', kind: 'text', sample: 'Tom' },
    { key: 'person.last_name', label: 'Last name', group: 'Person', kind: 'text', sample: 'Reyes' },
    { key: 'person.title', label: 'Job title', group: 'Person', kind: 'text', sample: 'Sales' },
    { key: 'person.email', label: 'Email', group: 'Person', kind: 'text', sample: 'tom.reyes@example.com' },
    { key: 'person.phone', label: 'Phone', group: 'Person', kind: 'text', sample: '(717) 555-0101' },
    { key: 'branch.name', label: 'Branch name', group: 'Branch', kind: 'text', sample: 'Harrisburg' },
    { key: 'branch.address', label: 'Branch address', group: 'Branch', kind: 'text',
        sample: '4410 Derry St, Harrisburg, PA 17111' },
    { key: 'branch.phone', label: 'Branch phone', group: 'Branch', kind: 'text', sample: '(717) 555-0142' },
    { key: 'branch.email', label: 'Branch email', group: 'Branch', kind: 'text', sample: 'harrisburg@example.com' },
    { key: 'branch.license', label: 'Branch license no.', group: 'Branch', kind: 'text', sample: 'PA-HIC 123456' },
    { key: 'company.name', label: 'Company name', group: 'Company', kind: 'text', sample: 'Your company' },
    { key: 'company.legal_name', label: 'Legal name', group: 'Company', kind: 'text', sample: 'Your company LLC' },
    { key: 'company.tagline', label: 'Tagline', group: 'Company', kind: 'text', sample: 'Lawns, beds and patios' },
    { key: 'company.website', label: 'Website', group: 'Company', kind: 'text', sample: 'example.com' },
    { key: 'company.phone', label: 'Company phone', group: 'Company', kind: 'text', sample: '(717) 555-0100' },
    { key: 'company.email', label: 'Company email', group: 'Company', kind: 'text', sample: 'office@example.com' },
    { key: 'company.address', label: 'Company address', group: 'Company', kind: 'text',
        sample: '4410 Derry St, Harrisburg, PA 17111' },
    { key: 'company.license', label: 'License no.', group: 'Company', kind: 'text', sample: 'PA-HIC 123456' },
    { key: 'company.logo', label: 'Logo', group: 'Company', kind: 'image', sample: '' },
    { key: 'company.colour', label: 'Brand colour', group: 'Company', kind: 'colour', sample: '' },
];
const FIELD_KEYS = new Set(SIGNATURE_FIELDS.map((field) => field.key));
export const signatureField = (key) => SIGNATURE_FIELDS.find((field) => field.key === key);
/** `{{person.name}}`, tolerating spaces inside the braces. */
const TOKEN = /\{\{\s*([a-z]+(?:\.[a-z_]+)?)\s*\}\}/g;
const TOKEN_ONLY = /^\{\{\s*([a-z]+(?:\.[a-z_]+)?)\s*\}\}$/;
const HAS_TOKEN = /\{\{\s*[a-z]+(?:\.[a-z_]+)?\s*\}\}/;
/** How a field is written into a signature. */
export const fieldToken = (key) => `{{${key}}}`;
/** The fields a piece of a signature names, known ones only. */
export function fieldsIn(html) {
    const found = new Set();
    for (const match of html.matchAll(TOKEN))
        if (FIELD_KEYS.has(match[1]))
            found.add(match[1]);
    return [...found];
}
/** Where a signature's own images are served, by file id — public, see the note at the top. */
export const EMAIL_IMAGE_PATH = '/api/v1/email-images/';
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const OWN_IMAGE = new RegExp(`^/api/v1/email-images/${UUID}$`, 'i');
/* Ours at some host — a signature pasted back from an email Rowted sent. Kept as the path. */
const OWN_IMAGE_ANYWHERE = new RegExp(`^https?://[^/\\s]+(/api/v1/email-images/${UUID})$`, 'i');
export const emailImagePath = (fileId) => `${EMAIL_IMAGE_PATH}${fileId}`;
/** Elements that never have content. */
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param',
    'source', 'track', 'wbr']);
/** Whose *contents* go too: a script's text is not text, and a style block is not words. */
const DROP_WITH_CONTENT = new Set(['script', 'style', 'textarea', 'title', 'iframe', 'object', 'noscript',
    'noembed', 'noframes', 'template', 'svg', 'math', 'select', 'head', 'xmp', 'frameset', 'frame', 'applet',
    'canvas', 'video', 'audio', 'picture', 'map']);
/** Containers that mean nothing in a fragment and are unwrapped without a word. */
const QUIET_UNWRAP = new Set(['html', 'body', 'o:p', 'center', 'article', 'section', 'header', 'footer', 'main']);
/** Opening one of these closes an open one of those, as a browser would (an unclosed `<td>`, `<li>`, `<p>`). */
const CLOSES = {
    td: ['td', 'th'], th: ['td', 'th'], tr: ['tr', 'td', 'th'], li: ['li'], p: ['p'],
    tbody: ['tbody', 'thead', 'tfoot', 'tr', 'td', 'th'], thead: ['tbody', 'thead', 'tfoot', 'tr', 'td', 'th'],
    tfoot: ['tbody', 'thead', 'tfoot', 'tr', 'td', 'th'],
};
const BLOCKS = new Set(['div', 'table', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'hr', 'p']);
/** A tag nested deeper than this was built to be expensive, not to be read. */
const MAX_DEPTH = 40;
/** Where a tag ends: the first `>` outside a quoted attribute. */
function tagEnd(html, from) {
    let quote = null;
    for (let i = from + 1; i < html.length; i += 1) {
        const ch = html[i];
        if (quote) {
            if (ch === quote)
                quote = null;
            continue;
        }
        if (ch === '"' || ch === '\'')
            quote = ch;
        else if (ch === '>')
            return i;
    }
    return -1;
}
/* Entities in an attribute, decoded so a scheme hidden as `&#106;avascript:` is seen for what it is. */
const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: '\'', nbsp: ' ' };
function decodeEntities(value) {
    return value.replace(/&(#\d{1,7}|#x[0-9a-f]{1,6}|[a-z]{2,8});?/gi, (whole, body) => {
        if (body[0] === '#') {
            const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
        }
        return NAMED[body.toLowerCase()] ?? whole;
    });
}
function parseAttrs(raw) {
    const out = [];
    const pattern = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
    for (const match of raw.matchAll(pattern)) {
        const name = match[1].toLowerCase();
        if (out.some((one) => one.name === name))
            continue;
        out.push({ name, value: decodeEntities(match[2] ?? match[3] ?? match[4] ?? '') });
    }
    return out;
}
/*
 * `&` is kept when it already starts an entity, so sanitising what was
 * sanitised before changes nothing (the note editor's rule, for the same
 * reason: an editor that saves twice must not grow a layer of escaping).
 */
const ENTITY = /^&(#\d{1,7}|#x[0-9a-f]{1,6}|[a-z][a-z0-9]{1,31});/i;
function escapeText(text) {
    let out = '';
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === '<') {
            out += '&lt;';
            continue;
        }
        if (ch === '>') {
            out += '&gt;';
            continue;
        }
        if (ch === '&') {
            const entity = ENTITY.exec(text.slice(i, i + 40));
            if (entity) {
                out += entity[0];
                i += entity[0].length - 1;
                continue;
            }
            out += '&amp;';
            continue;
        }
        out += ch;
    }
    return out;
}
/** Plain words into HTML: every `&`, `<` and `>` escaped, entities or not. */
const escapePlain = (text) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (value) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function parse(html, dropped) {
    const root = { t: 'el', name: '#root', attrs: [], kids: [] };
    const stack = [root];
    const top = () => stack[stack.length - 1];
    const pushText = (raw) => {
        if (!raw)
            return;
        const kids = top().kids;
        const last = kids[kids.length - 1];
        const v = escapeText(raw);
        if (last?.t === 'text')
            last.v += v;
        else
            kids.push({ t: 'text', v });
    };
    const lower = html.toLowerCase();
    let i = 0;
    while (i < html.length) {
        const lt = html.indexOf('<', i);
        if (lt === -1) {
            pushText(html.slice(i));
            break;
        }
        pushText(html.slice(i, lt));
        if (html.startsWith('<!--', lt)) {
            const end = html.indexOf('-->', lt + 4);
            i = end === -1 ? html.length : end + 3;
            continue;
        }
        const next = html[lt + 1] ?? '';
        if (next === '!' || next === '?') {
            const end = html.indexOf('>', lt);
            i = end === -1 ? html.length : end + 1;
            continue;
        }
        /* A `<` that cannot start a tag is a less-than sign: "5 < 6" is arithmetic, not markup. */
        if (!/[a-zA-Z/]/.test(next)) {
            pushText('<');
            i = lt + 1;
            continue;
        }
        const end = tagEnd(html, lt);
        if (end === -1) {
            pushText(html.slice(lt));
            break;
        }
        const raw = html.slice(lt + 1, end);
        i = end + 1;
        const closing = raw.startsWith('/');
        const name = (closing ? raw.slice(1) : raw).trim().split(/[\s/>]/)[0].toLowerCase();
        if (!name)
            continue;
        if (closing) {
            for (let at = stack.length - 1; at > 0; at -= 1) {
                if (stack[at].name === name) {
                    stack.length = at;
                    break;
                }
            }
            continue;
        }
        const selfClosed = raw.trimEnd().endsWith('/');
        if (DROP_WITH_CONTENT.has(name)) {
            dropped.add(name);
            if (selfClosed || VOID.has(name))
                continue;
            const close = lower.indexOf(`</${name}`, i);
            i = close === -1 ? html.length : (html.indexOf('>', close) + 1 || html.length);
            continue;
        }
        const closes = CLOSES[name];
        if (closes)
            while (stack.length > 1 && closes.includes(top().name))
                stack.pop();
        if (BLOCKS.has(name) && top().name === 'p')
            stack.pop();
        if (stack.length > MAX_DEPTH)
            continue;
        const nameEnd = /^[^\s/>]+/.exec(raw)?.[0].length ?? 0;
        const element = { t: 'el', name, attrs: parseAttrs(raw.slice(nameEnd)), kids: [] };
        top().kids.push(element);
        if (!VOID.has(name) && !selfClosed)
            stack.push(element);
    }
    return root;
}
// ---------------------------------------------------------------------------
// The allow-list
// ---------------------------------------------------------------------------
const TABLE_CELL = ['width', 'height', 'align', 'valign', 'bgcolor', 'colspan', 'rowspan', 'nowrap'];
/** What each element may carry beyond `style`, `title` and `dir`. */
const ELEMENTS = {
    a: ['href', 'target'],
    b: [], strong: [], i: [], em: [], u: [], s: [], strike: [], small: [], sub: [], sup: [], span: [], br: [],
    p: ['align'], div: ['align'], h1: ['align'], h2: ['align'], h3: ['align'], h4: ['align'],
    ul: [], ol: [], li: [], blockquote: [],
    hr: ['width', 'size', 'color', 'align', 'noshade'],
    font: ['color', 'face', 'size'],
    img: ['src', 'alt', 'width', 'height', 'border', 'align'],
    table: ['width', 'height', 'cellpadding', 'cellspacing', 'border', 'align', 'bgcolor', 'role'],
    thead: ['align', 'valign'], tbody: ['align', 'valign'], tfoot: ['align', 'valign'],
    tr: ['align', 'valign', 'bgcolor', 'height'],
    td: TABLE_CELL, th: TABLE_CELL,
    colgroup: ['span', 'width'], col: ['span', 'width'],
};
const GLOBAL_ATTRS = ['style', 'title', 'dir'];
const BOOLEAN_ATTRS = new Set(['nowrap', 'noshade']);
const SMALL_NUMBER = /^\d{1,3}$/;
const LENGTH = /^\d{1,4}(\.\d{1,2})?(px|%)?$/;
const COLOUR = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.%\s,/]+\)|[a-z]{3,24})$/i;
const FONT_FACE = /^[\w\s,'"-]{1,200}$/;
/** A style property an email can carry. Positioning, animation and anything that loads are not. */
const CSS_PROPERTY = new RegExp('^(color|background(-color)?|font(-(family|size|weight|style|variant))?'
    + '|text-(decoration|align|transform|indent)|letter-spacing|line-height|vertical-align|white-space'
    + '|word-(break|wrap|spacing)|overflow-wrap|(min-|max-)?(width|height)|margin(-(top|right|bottom|left))?'
    + '|padding(-(top|right|bottom|left))?|border(-[a-z]+){0,2}|display|table-layout|list-style(-type)?|float'
    + '|mso-[a-z-]+)$');
const CSS_DANGER = /url\s*\(|expression|javascript|vbscript|@import|behavior|-moz-binding|[<>\\]|\/\*|\*\//i;
function cleanUrl(value, schemes, ctx) {
    const clean = [...value].filter((ch) => ch.codePointAt(0) > 0x20).join('');
    if (clean.length === 0 || clean.length > 2000)
        return null;
    if (!ctx.final && (TOKEN_ONLY.test(clean) || /^(mailto:|tel:)\{\{/i.test(clean)))
        return clean;
    return schemes.test(clean) ? clean : null;
}
function cleanSrc(value, ctx) {
    const clean = value.trim();
    if (!ctx.final) {
        const token = TOKEN_ONLY.exec(clean);
        if (token && signatureField(token[1])?.kind === 'image')
            return `{{${token[1]}}}`;
    }
    if (OWN_IMAGE.test(clean))
        return clean;
    if (!ctx.final) {
        const pasted = OWN_IMAGE_ANYWHERE.exec(clean);
        if (pasted)
            return pasted[1];
    }
    if (ctx.final && ctx.imageBase && clean.startsWith(ctx.imageBase)
        && OWN_IMAGE.test(clean.slice(ctx.imageBase.length)))
        return clean;
    if (ctx.images === 'any' && /^https?:\/\/[^\s"'<>]+$/i.test(clean))
        return clean;
    return null;
}
function cleanStyle(value, ctx) {
    const out = [];
    /* `;` inside quotes or parentheses is not a separator (`font-family: "A;B"`, `rgb(1, 2, 3)`). */
    const declarations = [];
    let current = '';
    let depth = 0;
    let quote = null;
    for (const ch of value) {
        if (quote) {
            if (ch === quote)
                quote = null;
            current += ch;
            continue;
        }
        if (ch === '"' || ch === '\'')
            quote = ch;
        else if (ch === '(')
            depth += 1;
        else if (ch === ')')
            depth = Math.max(0, depth - 1);
        else if (ch === ';' && depth === 0) {
            declarations.push(current);
            current = '';
            continue;
        }
        current += ch;
    }
    declarations.push(current);
    for (const declaration of declarations) {
        const at = declaration.indexOf(':');
        if (at < 1)
            continue;
        const property = declaration.slice(0, at).trim().toLowerCase();
        const raw = declaration.slice(at + 1).trim().replace(/\s+/g, ' ');
        if (!raw)
            continue;
        if (!CSS_PROPERTY.test(property)) {
            ctx.dropped.add(`the ${property} style`);
            continue;
        }
        if (raw.length > 300 || CSS_DANGER.test(raw)) {
            ctx.dropped.add(`a ${property} that loads or runs something`);
            continue;
        }
        if (ctx.final && HAS_TOKEN.test(raw))
            continue;
        out.push(`${property}: ${raw.replace(TOKEN, (_whole, key) => `{{${key}}}`)}`);
    }
    return out.length > 0 ? out.join('; ') : null;
}
function cleanAttr(element, name, value, ctx) {
    if (name === 'style')
        return cleanStyle(value, ctx);
    if (name === 'href')
        return cleanUrl(value, /^(https?:\/\/|mailto:|tel:)/i, ctx);
    if (name === 'src')
        return cleanSrc(value, ctx);
    if (name === 'title' || name === 'alt')
        return value.slice(0, 300);
    if (name === 'dir')
        return /^(ltr|rtl|auto)$/i.test(value) ? value.toLowerCase() : null;
    if (name === 'target')
        return value.toLowerCase() === '_blank' ? '_blank' : null;
    if (name === 'role')
        return value === 'presentation' ? value : null;
    if (name === 'align')
        return /^(left|center|right|justify)$/i.test(value) ? value.toLowerCase() : null;
    if (name === 'valign')
        return /^(top|middle|bottom|baseline)$/i.test(value) ? value.toLowerCase() : null;
    if (name === 'width' || name === 'height')
        return LENGTH.test(value.trim()) ? value.trim() : null;
    if (name === 'size' && element === 'font')
        return /^[1-7]$/.test(value.trim()) ? value.trim() : null;
    if (['cellpadding', 'cellspacing', 'border', 'colspan', 'rowspan', 'span', 'size'].includes(name)) {
        return SMALL_NUMBER.test(value.trim()) ? value.trim() : null;
    }
    if (name === 'bgcolor' || name === 'color') {
        const clean = value.trim();
        if (!ctx.final && TOKEN_ONLY.test(clean))
            return clean;
        return COLOUR.test(clean) ? clean : null;
    }
    if (name === 'face')
        return FONT_FACE.test(value) ? value : null;
    if (BOOLEAN_ATTRS.has(name))
        return '';
    return null;
}
function serialise(nodes, ctx) {
    let out = '';
    for (const node of nodes) {
        if (node.t === 'text') {
            out += node.v;
            continue;
        }
        const allowed = ELEMENTS[node.name];
        if (!allowed) {
            if (!QUIET_UNWRAP.has(node.name) && node.name !== '#root')
                ctx.dropped.add(node.name);
            out += serialise(node.kids, ctx);
            continue;
        }
        let attrs = '';
        let src = null;
        let href = null;
        for (const attr of node.attrs) {
            if (attr.name.startsWith('on')) {
                ctx.dropped.add('event handlers');
                continue;
            }
            if (!GLOBAL_ATTRS.includes(attr.name) && !allowed.includes(attr.name))
                continue;
            const value = cleanAttr(node.name, attr.name, attr.value, ctx);
            if (value === null) {
                if (attr.name === 'src')
                    ctx.dropped.add('images from outside Rowted');
                else if (attr.name === 'href')
                    ctx.dropped.add('links that are not web, email or phone');
                continue;
            }
            if (attr.name === 'src')
                src = value;
            if (attr.name === 'href')
                href = value;
            attrs += BOOLEAN_ATTRS.has(attr.name) ? ` ${attr.name}` : ` ${attr.name}="${escapeAttr(value)}"`;
        }
        /* A picture with nowhere to come from is not a picture; a link with nowhere to go is its words. */
        if (node.name === 'img' && !src)
            continue;
        if (node.name === 'a' && !href) {
            out += serialise(node.kids, ctx);
            continue;
        }
        if (node.name === 'a' && / target="_blank"/.test(attrs))
            attrs += ' rel="noopener noreferrer"';
        out += `<${node.name}${attrs}>`;
        if (VOID.has(node.name))
            continue;
        out += `${serialise(node.kids, ctx)}</${node.name}>`;
    }
    return out;
}
/** Tokens written `{{ person.name }}` read `{{person.name}}`, so two spellings are one signature. */
const tidyTokens = (html) => html.replace(TOKEN, (_whole, key) => `{{${key}}}`);
/** A signature's HTML with everything not on the allow-list removed, and what that was. */
export function sanitizeSignature(html, options = {}) {
    const dropped = new Set();
    const ctx = { final: false, images: options.images ?? 'ours', imageBase: '', dropped };
    const out = tidyTokens(serialise(parse(html.slice(0, SIGNATURE_MAX_CHARS * 2), dropped).kids, ctx)).trim();
    return { html: out, dropped: [...dropped] };
}
/** The stored form: sanitised, merge fields kept. */
export const sanitizeSignatureHtml = (html) => sanitizeSignature(html).html;
/** What a stored signature may be, at most — far past any real one, short of a page of somebody's novel. */
export const SIGNATURE_MAX_CHARS = 20_000;
/** Whether a signature says anything at all once the markup is off (an image counts). */
export function signatureIsEmpty(html) {
    if (/<img\b/i.test(html))
        return false;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/gi, ' ').trim().length === 0;
}
// ---------------------------------------------------------------------------
// Rendering for one person
// ---------------------------------------------------------------------------
const isEmpty = (value) => !value || value.trim().length === 0;
function fieldsOfNodes(nodes, into = new Set()) {
    for (const node of nodes) {
        if (node.t === 'text') {
            for (const match of node.v.matchAll(TOKEN))
                if (FIELD_KEYS.has(match[1]))
                    into.add(match[1]);
            continue;
        }
        for (const attr of node.attrs) {
            for (const match of attr.value.matchAll(TOKEN))
                if (FIELD_KEYS.has(match[1]))
                    into.add(match[1]);
        }
        fieldsOfNodes(node.kids, into);
    }
    return into;
}
/** Named fields, every one of them empty for this person. */
function allEmpty(nodes, values) {
    const fields = fieldsOfNodes(nodes);
    return fields.size > 0 && [...fields].every((key) => isEmpty(values[key]));
}
/** What may be left out whole when its fields are empty: a line, and the wrappers inside one. */
const DROPPABLE = new Set(['p', 'div', 'tr', 'li', 'h1', 'h2', 'h3', 'h4', 'table', 'a', 'img', 'span', 'b',
    'strong', 'i', 'em', 'u', 'font', 'small']);
function prune(element, values) {
    /* Runs between `<br>`s are lines too. */
    if (element.kids.some((kid) => kid.t === 'el' && kid.name === 'br')) {
        const runs = [{ nodes: [], br: null }];
        for (const kid of element.kids) {
            if (kid.t === 'el' && kid.name === 'br') {
                runs[runs.length - 1].br = kid;
                runs.push({ nodes: [], br: null });
            }
            else {
                runs[runs.length - 1].nodes.push(kid);
            }
        }
        const kept = runs.filter((run) => !allEmpty(run.nodes, values));
        /*
         * Every kept line but the last keeps its break. The last never does: a
         * line dropped after it took the break that led to it, and when the last
         * line was kept it never had one.
         */
        element.kids = kept.flatMap((run, index) => (index < kept.length - 1 && run.br ? [...run.nodes, run.br] : run.nodes));
    }
    element.kids = element.kids.filter((kid) => !(kid.t === 'el' && DROPPABLE.has(kid.name) && allEmpty([kid], values)));
    for (const kid of element.kids)
        if (kid.t === 'el')
            prune(kid, values);
}
/** A separator a field sits beside: ` · `, ` | `, ` • `, ` – `, ` - `, `, `. */
const SEP_BEFORE = /\s*(?:[·|•–—-]|,)\s*$/;
const SEP_AFTER = /^\s*(?:[·|•–—-]|,)\s*/;
/** Words: each field's value escaped in, and an empty one taking its separator with it. */
function fillText(text, values) {
    let out = '';
    let rest = text;
    const one = /\{\{\s*([a-z]+(?:\.[a-z_]+)?)\s*\}\}/;
    for (;;) {
        const match = one.exec(rest);
        if (!match) {
            out += rest;
            break;
        }
        const key = match[1];
        const before = rest.slice(0, match.index);
        let after = rest.slice(match.index + match[0].length);
        if (!FIELD_KEYS.has(key)) {
            out += before + match[0];
            rest = after;
            continue;
        }
        const value = values[key];
        if (isEmpty(value)) {
            /* The separator in front of it goes; failing that, the one after it — never both sides' words. */
            if (SEP_BEFORE.test(before)) {
                out += before.replace(SEP_BEFORE, '');
            }
            else {
                out += before;
                after = after.replace(SEP_AFTER, '');
            }
        }
        else {
            out += before + escapePlain(value);
        }
        rest = after;
    }
    return out;
}
/** A link from a field: a bare address is a website, an email address is `mailto:`, digits are `tel:`. */
function hrefOf(key, value) {
    const clean = value.trim();
    if (/^(https?:\/\/|mailto:|tel:)/i.test(clean))
        return clean;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean))
        return `mailto:${clean}`;
    if (/^[+\d][\d\s().-]{5,}$/.test(clean) && /phone/.test(key))
        return `tel:${clean.replace(/[^\d+]/g, '')}`;
    return `https://${clean.replace(/^\/+/, '')}`;
}
function fillAttrs(element, values, imageBase) {
    for (const attr of element.attrs) {
        const only = TOKEN_ONLY.exec(attr.value.trim());
        if (attr.name === 'href' && only) {
            const value = values[only[1]];
            attr.value = isEmpty(value) ? '' : hrefOf(only[1], value);
            continue;
        }
        attr.value = attr.value.replace(TOKEN, (whole, key) => {
            if (!FIELD_KEYS.has(key))
                return whole;
            const value = values[key] ?? '';
            if (attr.name === 'href' && /^(mailto:|tel:)/i.test(attr.value)) {
                return key.endsWith('phone') ? value.replace(/[^\d+]/g, '') : encodeURI(value.trim());
            }
            return value;
        });
        if (attr.name === 'src' && attr.value.startsWith(EMAIL_IMAGE_PATH))
            attr.value = `${imageBase}${attr.value}`;
    }
}
function fill(nodes, values, imageBase) {
    for (const node of nodes) {
        if (node.t === 'text') {
            node.v = fillText(node.v, values);
            continue;
        }
        fillAttrs(node, values, imageBase);
        fill(node.kids, values, imageBase);
    }
}
/**
 * A signature as one person's email ends: every field filled, every line whose
 * fields are all empty left out, and the result sanitised once more — what a
 * field holds is somebody's data, and a link built from it is checked like any
 * other.
 */
export function renderSignature(html, values, options = {}) {
    const imageBase = (options.imageBase ?? '').replace(/\/$/, '');
    const dropped = new Set();
    const root = parse(sanitizeSignatureHtml(html), dropped);
    prune(root, values);
    fill(root.kids, values, imageBase);
    const ctx = { final: true, images: 'ours', imageBase, dropped };
    return serialise(root.kids, ctx).trim();
}
/** The sample values, for a template card and a preview with nobody picked. */
export const SAMPLE_SIGNATURE_VALUES = Object.fromEntries(SIGNATURE_FIELDS.filter((field) => field.sample).map((field) => [field.key, field.sample]));
// ---------------------------------------------------------------------------
// Templates (CodeTwo's starting points, in the app's own four)
// ---------------------------------------------------------------------------
const FONT = 'font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.5; color: rgb(29, 29, 26)';
const LINK = 'color: {{company.colour}}; text-decoration: none';
/**
 * Written in the sanitiser's own form — `prop: value; prop: value`, attributes
 * in the order they are allowed — so `sanitizeSignatureHtml(template)` is the
 * template, character for character (`tests/email-signature.test.ts`).
 */
export const SIGNATURE_TEMPLATES = [
    {
        key: 'simple', name: 'Simple', sub: 'Name, title, company, phone and email',
        html: `<p style="margin: 0; ${FONT}"><b>{{person.name}}</b><br>{{person.title}}<br>{{company.name}}<br>`
            + `{{person.phone}}<br><a href="mailto:{{person.email}}" style="${LINK}">{{person.email}}</a></p>`,
    },
    {
        key: 'logo', name: 'With logo', sub: 'The logo beside the details, a rule in the brand colour',
        html: `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="${FONT}"><tbody><tr>`
            + '<td valign="top" style="padding-right: 14px; border-right: 2px solid {{company.colour}}">'
            + '<img src="{{company.logo}}" alt="{{company.name}}" width="120" style="display: block; border: 0"></td>'
            + '<td valign="top" style="padding-left: 14px"><b style="font-size: 14px">{{person.name}}</b><br>'
            + '{{person.title}}<br>{{company.name}}<br>{{person.phone}}<br>'
            + `<a href="mailto:{{person.email}}" style="${LINK}">{{person.email}}</a><br>`
            + `<a href="{{company.website}}" style="${LINK}">{{company.website}}</a></td></tr></tbody></table>`,
    },
    {
        key: 'branch', name: 'With branch', sub: 'The person, then the branch they work from',
        html: `<p style="margin: 0; ${FONT}"><b>{{person.name}}</b><br>{{person.title}}<br>`
            + '<b style="color: {{company.colour}}">{{company.name}}</b> · {{branch.name}}<br>{{branch.address}}<br>'
            + `{{branch.phone}} · <a href="mailto:{{person.email}}" style="${LINK}">{{person.email}}</a></p>`,
    },
    {
        key: 'blank', name: 'Blank', sub: 'Just the name, to build on',
        html: `<p style="margin: 0; ${FONT}">{{person.name}}</p>`,
    },
];
/** What every company starts with (migration 0143, `seedTenant`, signup): *Simple*. */
export const DEFAULT_SIGNATURE_NAME = 'Company signature';
export const DEFAULT_SIGNATURE_HTML = SIGNATURE_TEMPLATES[0].html;
// ---------------------------------------------------------------------------
// Replies that look like replies (2.5)
// ---------------------------------------------------------------------------
/** A subject without its *Re:* and *Fwd:* — what makes two emails one thread. */
export function threadSubject(subject) {
    let base = (subject ?? '').trim();
    for (;;) {
        const next = base.replace(/^(re|fw|fwd|aw|wg|sv|antw)\s*(\[\d+\])?\s*:\s*/i, '');
        if (next === base)
            break;
        base = next;
    }
    return base.replace(/\s+/g, ' ').toLowerCase();
}
/** *Tue, Oct 20, 2026 at 9:45 AM*, in the zone given. */
export function quoteWhen(at, timeZone) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
        timeZone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric',
        minute: '2-digit', hour12: true,
    }).formatToParts(at).map((part) => [part.type, part.value]));
    return `${parts.weekday}, ${parts.month} ${parts.day}, ${parts.year} at ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
}
/** *On Tue, Oct 20, 2026 at 9:45 AM, Renee Castillo <renee@example.com> wrote:* */
export function quoteHeader(at, name, address, timeZone) {
    const who = [name.trim(), address ? `<${address}>` : ''].filter(Boolean).join(' ') || address || 'Somebody';
    return `On ${quoteWhen(at, timeZone)}, ${who} wrote:`;
}
/** At most this many earlier messages go under a reply; past it a thread is an archive. */
export const QUOTE_DEPTH = 10;
/**
 * The earlier messages, newest first, each quoted inside the one after it —
 * `gmail_quote`, because that is the class Gmail folds under its *…* and
 * every other client simply shows.
 */
export function quotedHistoryHtml(messages, timeZone) {
    let inner = '';
    for (const message of [...messages.slice(0, QUOTE_DEPTH)].reverse()) {
        const header = escapePlain(quoteHeader(message.at, message.name, message.address, timeZone));
        inner = '<div class="gmail_quote">'
            + `<div dir="ltr" class="gmail_attr">${header}<br></div>`
            + '<blockquote class="gmail_quote" style="margin: 0px 0px 0px 0.8ex; border-left: 1px solid rgb(204, 204, 204); '
            + 'padding-left: 1ex">'
            + `${message.html}${inner ? `<br><br>${inner}` : ''}</blockquote></div>`;
    }
    return inner;
}
/**
 * The email that leaves: what the person wrote, their signature, and the
 * thread under it. `body` is already HTML (`textToHtml`), the signature is
 * rendered, the quote is `quotedHistoryHtml`'s.
 */
export function replyEmailHtml(parts) {
    return [
        parts.body,
        parts.signature ? `<br><br><div>${parts.signature}</div>` : '',
        parts.quote ? `<br>${parts.quote}` : '',
    ].join('');
}
// ---------------------------------------------------------------------------
// Automatic emails (Aaron, Oct 25, answer 2.6: "you should be able to choose
// whether signatures are on confirmations and invoices and which ones they use")
// ---------------------------------------------------------------------------
/**
 * The automatic emails Rowted sends a customer today, in the order the page
 * lists them: a booked site visit's confirmation (`requests/create.ts`) and
 * an invoice (`collections.ts`). Quotes, visit reminders and job updates are
 * notification kinds nothing emails yet; each joins this list the day
 * something does, so the page never offers a switch for a message that is
 * never sent. Keys are `NotificationEvent`s, `communications.event_type`.
 */
export const AUTOMATIC_EMAIL_KINDS = ['appointments', 'invoices'];
export const AUTOMATIC_EMAIL_WORDS = {
    appointments: { label: 'Appointment confirmations', sub: 'the email a booked site visit sends' },
    invoices: { label: 'Invoices', sub: 'an invoice emailed to its bill-to contact' },
};
export const isAutomaticEmailKind = (value) => AUTOMATIC_EMAIL_KINDS.includes(value ?? '');
/** *Uses Sales signature — from the Sales role*, the preview's one line. */
export function signatureUseWords(use, signatureName) {
    if (use.from === 'none')
        return use.roleName ? `No signature — from the ${use.roleName} role` : 'No signature';
    const name = signatureName ?? 'a signature';
    if (use.from === 'person')
        return `Uses ${name} — their own`;
    if (use.from === 'role')
        return `Uses ${name} — from the ${use.roleName ?? ''} role`;
    return `Uses ${name} — the company default`;
}
//# sourceMappingURL=email-signature.js.map